import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Location } from '@angular/common';

export interface CarouselElement {
	id: string;
	label: string;
}

@Component({
	selector: 'lvr-nav-carousel',
	imports: [RouterModule, MatButtonModule, MatIconModule, MatTooltipModule],
	templateUrl: './nav-carousel.html',
	styleUrl: './nav-carousel.scss',
})
/**
 * A reusable carousel component for navigation through a list of elements.
 * It emits the current events on navigation and handles the state internally.
 */
export class NavCarousel {
	private readonly route = inject(ActivatedRoute);
	private readonly location = inject(Location);
	private readonly router = inject(Router);

	/**
	 * Label for the back button.
	 * Default is set to 'Zurück'
	 */
	public readonly backLabel = input<string>('Zurück');
	/**
	 * Route to navigate when clicking the back button.
	 * Example: '/desk'
	 */
	public readonly backLink = input.required<string>();
	/**
	 * If true, the back button uses redirectUrl or browser history.
	 * Default is false to keep backward compatibility.
	 */
	public readonly useDynamicBack = input<boolean>(false);
	/**
	 * The elements to display and navigate in the carousel.
	 */
	public readonly carouselElements = input.required<CarouselElement[]>();
	/**
	 * Emits the current index and element when navigation occurs.
	 */
	public readonly navigation = output<{ index: number; element: CarouselElement }>();

	/**
	 * Current internal index of the carousel.
	 * Default to 0.
	 */
	public readonly index = signal(0);
	/**
	 * Initial index to set when the component is initialized.
	 * Defaults to `0`.
	 */
	public readonly initialIndex = input<number>(0);
	/**
	 * Element currently active in the carousel.
	 */
	public readonly currentElement = computed(() => {
		const elements = this.carouselElements();
		const index = this.index();
		return index >= 0 && index < elements.length ? elements[index] : undefined;
	});

	public readonly showCarousel = input<boolean>(true);
	/**
	 * Whether or not the breadcrumb text should be shown or not.
	 */
	public readonly showBreadcrumb = input<boolean>(false);
	/**
	 * Breadcrumb text before the navigation controls.
	 * Default is an empty string.
	 */
	public readonly breadcrumb = input<string>('');
	/**
	 * Whether the current element is the first in the list.
	 */
	public readonly isFirst = computed(() => this.index() === 0);
	/**
	 *Whether the current element is the last in the list.
	 */
	public readonly isLast = computed(() => {
		const elements = this.carouselElements();
		return elements.length === 0 || this.index() === elements.length - 1;
	});

	/**
	 * Initializes the component by setting the initial index.
	 */
	public constructor() {
		effect(() => {
			const initial = this.initialIndex();
			const max = this.carouselElements().length;
			// Makes sure index is within the list limits
			if (initial >= 0 && initial < max) {
				this.index.set(initial);
			}
		});
	}

	public onBackClick() {
		if (!this.useDynamicBack()) {
			this.router.navigateByUrl(this.backLink());
			return;
		}

		const redirectUrl = this.route.snapshot.queryParamMap.get('redirectUrl');

		if (redirectUrl && redirectUrl.startsWith('/')) {
			this.router.navigateByUrl(redirectUrl);
			return;
		}

		if (window.history.length > 1) {
			this.location.back();
			return;
		}

		this.router.navigateByUrl(this.backLink());
	}

	/**
	 * Navigates to the previous element, updates the index,
	 * and emits the new current element and index.
	 */
	public goToPrevious(): void {
		if (!this.isFirst()) {
			this.index.update((i) => i - 1);
			this.emitCurrent();
		}
	}

	/**
	 * Navigates to the next element, updates the index,
	 * and emits the new current element and index.
	 */
	public goToNext(): void {
		if (!this.isLast()) {
			this.index.update((i) => i + 1);
			this.emitCurrent();
		}
	}

	/**
	 * Emits the current index and element via the navigation output.
	 */
	private emitCurrent() {
		const index = this.index();
		const element = this.carouselElements()[index];
		this.navigation.emit({ index: index, element: element });
	}
}
